/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { FileInfo, FileType } from "Api";
import { QuickFormFile, TransformStatus, UploadStatus } from "./QuickFormTypes";
import { ValidationResult } from "./QuickFormValidationRules";

export class QuickFormState {
  files: QuickFormFile[] = [];
  allSelected: boolean = false;
  currentPage: number = 1;
  itemsPerPage: number = 10;

  constructor() {
    this.files = [];
    this.allSelected = false;
    this.currentPage = 1;
    this.itemsPerPage = 10;
  }
}

export type QuickFormAction =
  | AddFilesAction
  | AddQuickFormFilesAction
  | RemoveFileAction
  | UpdateFileUploadProgressAction
  | UpdateFileUploadStatusAction
  | UpdateFileDetectedTypeAction
  | UpdateFileTransformToAction
  | UpdateFileTransformStatusAction
  | ToggleFileSelectionAction
  | ToggleAllSelectionAction
  | BulkSetTransformTypeAction
  | BulkSetDetectedTypeAction
  | SetPageAction
  | ClearFilesAction
  | SetAllTransformingAction
  | UpdateTransformResultsAction
  | UpdateFileValidationAction;

type AddFilesAction = [
  "addFiles",
  {
    fileInfos: FileInfo[];
  }
];

type AddQuickFormFilesAction = [
  "addQuickFormFiles",
  {
    files: QuickFormFile[];
  }
];

type RemoveFileAction = [
  "removeFile",
  {
    fileId: string;
  }
];

type UpdateFileUploadProgressAction = [
  "updateFileUploadProgress",
  {
    fileId: string;
    progress: number;
  }
];

type UpdateFileUploadStatusAction = [
  "updateFileUploadStatus",
  {
    fileId: string;
    status: UploadStatus;
  }
];

type UpdateFileDetectedTypeAction = [
  "updateFileDetectedType",
  {
    fileId: string;
    detectedType: FileType;
  }
];

type UpdateFileTransformToAction = [
  "updateFileTransformTo",
  {
    fileId: string;
    transformTo: FileType | null;
  }
];

type UpdateFileTransformStatusAction = [
  "updateFileTransformStatus",
  {
    fileId: string;
    status: TransformStatus;
    errorMessage?: string;
  }
];

type ToggleFileSelectionAction = [
  "toggleFileSelection",
  {
    fileId: string;
  }
];

type ToggleAllSelectionAction = ["toggleAllSelection"];

type BulkSetTransformTypeAction = [
  "bulkSetTransformType",
  {
    transformType: FileType;
  }
];

type SetPageAction = [
  "setPage",
  {
    page: number;
  }
];

type UpdateFileValidationAction = [
  "updateFileValidation",
  {
    fileId: string;
    validationResult: ValidationResult;
  }
];

type BulkSetDetectedTypeAction = [
  "bulkSetDetectedType",
  {
    detectedType: FileType;
  }
];

type ClearFilesAction = ["clearFiles"];

export function QuickFormReducer(
  state: QuickFormState,
  action: QuickFormAction
): QuickFormState {
  const actionType = action[0];

  switch (actionType) {
    case "addFiles":
      return addFiles(state, action[1].fileInfos);
    case "addQuickFormFiles":
      return addQuickFormFiles(state, action[1].files);
    case "removeFile":
      return removeFile(state, action[1].fileId);
    case "updateFileUploadProgress":
      return updateFileUploadProgress(
        state,
        action[1].fileId,
        action[1].progress
      );
    case "updateFileUploadStatus":
      return updateFileUploadStatus(state, action[1].fileId, action[1].status);
    case "updateFileDetectedType":
      return updateFileDetectedType(
        state,
        action[1].fileId,
        action[1].detectedType
      );
    case "updateFileTransformTo":
      return updateFileTransformTo(
        state,
        action[1].fileId,
        action[1].transformTo
      );
    case "updateFileTransformStatus":
      return updateFileTransformStatus(
        state,
        action[1].fileId,
        action[1].status,
        action[1].errorMessage
      );
    case "toggleFileSelection":
      return toggleFileSelection(state, action[1].fileId);
    case "toggleAllSelection":
      return toggleAllSelection(state);
    case "bulkSetTransformType":
      return bulkSetTransformType(state, action[1].transformType);
    case "setPage":
      return setPage(state, action[1].page);
    case "clearFiles":
      return clearFiles(state);
    case "setAllTransforming":
      return setAllTransforming(state);
    case "updateTransformResults":
      return updateTransformResults(state, action[1].results);
    case "updateFileValidation":
      return updateFileValidation(
        state,
        action[1].fileId,
        action[1].validationResult
      );
    case "bulkSetDetectedType":
      return bulkSetDetectedType(state, action[1].detectedType);
    default:
      return state;
  }
}

function addFiles(
  state: QuickFormState,
  fileInfos: FileInfo[]
): QuickFormState {
  const newFiles: QuickFormFile[] = fileInfos.map((fileInfo) => ({
    id: fileInfo.id,
    fileInfo,
    detectedType:
      fileInfo.predictedFileType || fileInfo.userSelectedFileType || null,
    transformTo: null,
    uploadStatus: UploadStatus.COMPLETE,
    uploadProgress: 100,
    transformStatus: TransformStatus.PENDING,
    selected: false,
  }));

  return {
    ...state,
    files: [...state.files, ...newFiles],
  };
}

function addQuickFormFiles(
  state: QuickFormState,
  files: QuickFormFile[]
): QuickFormState {
  return {
    ...state,
    files: [...state.files, ...files],
  };
}

function removeFile(state: QuickFormState, fileId: string): QuickFormState {
  return {
    ...state,
    files: state.files.filter((file) => file.id !== fileId),
  };
}

function updateFileUploadProgress(
  state: QuickFormState,
  fileId: string,
  progress: number
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.id === fileId ? { ...file, uploadProgress: progress } : file
    ),
  };
}

function updateFileUploadStatus(
  state: QuickFormState,
  fileId: string,
  status: UploadStatus
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.id === fileId ? { ...file, uploadStatus: status } : file
    ),
  };
}

function updateFileDetectedType(
  state: QuickFormState,
  fileId: string,
  detectedType: FileType
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.id === fileId ? { ...file, detectedType } : file
    ),
  };
}

function updateFileTransformTo(
  state: QuickFormState,
  fileId: string,
  transformTo: FileType | null
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.id === fileId ? { ...file, transformTo } : file
    ),
  };
}

function updateFileTransformStatus(
  state: QuickFormState,
  fileId: string,
  status: TransformStatus,
  errorMessage?: string
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.id === fileId
        ? { ...file, transformStatus: status, errorMessage }
        : file
    ),
  };
}

function toggleFileSelection(
  state: QuickFormState,
  fileId: string
): QuickFormState {
  const newFiles = state.files.map((file) =>
    file.id === fileId ? { ...file, selected: !file.selected } : file
  );

  const allSelected = newFiles.every((file) => file.selected);

  return {
    ...state,
    files: newFiles,
    allSelected,
  };
}

function toggleAllSelection(state: QuickFormState): QuickFormState {
  const newAllSelected = !state.allSelected;

  return {
    ...state,
    allSelected: newAllSelected,
    files: state.files.map((file) => ({
      ...file,
      selected: newAllSelected,
    })),
  };
}

function bulkSetTransformType(
  state: QuickFormState,
  transformType: FileType
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.selected ? { ...file, transformTo: transformType } : file
    ),
  };
}

function setPage(state: QuickFormState, page: number): QuickFormState {
  return {
    ...state,
    currentPage: page,
  };
}

function clearFiles(state: QuickFormState): QuickFormState {
  return {
    ...state,
    files: [],
    allSelected: false,
    currentPage: 1,
  };
}

type SetAllTransformingAction = ["setAllTransforming"];

type UpdateTransformResultsAction = [
  "updateTransformResults",
  {
    results: Array<{
      id: string;
      transformSuccess: boolean;
      errorMessage?: string;
    }>;
  }
];

function setAllTransforming(state: QuickFormState): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) => ({
      ...file,
      transformStatus: TransformStatus.PROCESSING,
    })),
  };
}

function updateTransformResults(
  state: QuickFormState,
  results: Array<{
    id: string;
    transformSuccess: boolean;
    errorMessage?: string;
  }>
): QuickFormState {
  const resultMap = new Map(results.map((r) => [r.id, r]));

  return {
    ...state,
    files: state.files.map((file) => {
      const result = resultMap.get(file.id);
      if (!result) return file;

      return {
        ...file,
        transformStatus: result.transformSuccess
          ? TransformStatus.SUCCESS
          : TransformStatus.FAILED,
        errorMessage: result.errorMessage,
      };
    }),
  };
}

function updateFileValidation(
  state: QuickFormState,
  fileId: string,
  validationResult: ValidationResult
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.id === fileId ? { ...file, validationResult } : file
    ),
  };
}

function bulkSetDetectedType(
  state: QuickFormState,
  detectedType: FileType
): QuickFormState {
  return {
    ...state,
    files: state.files.map((file) =>
      file.selected ? { ...file, detectedType } : file
    ),
  };
}
